import {Control, RegisterOptions, useFieldArray, useFormState, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {hasTariffFlag, userSelector} from 'entities/user';
import {
    getHeaderNameError,
    getHeadersErrorMessage,
    isHostLocked,
    isUserAgentLocked,
    REQUEST_HEADERS_MAX,
    validateHeaderName,
} from 'features/http/lib/requestHeaders';
import {IRequestHeaderRow, RequestHeadersMode} from 'features/http/model/HttpMonitor/IRequestHeader';

const EMPTY_ROW: IRequestHeaderRow = {name: '', value: ''};

export const useRequestHeaders = (control: Control<any>, selective = false) => {
    const user = useSelector(userSelector);
    const userAgentEnabled = hasTariffFlag(user, 'monitor_custom_user_agent_enabled');
    const customHostEnabled = hasTariffFlag(user, 'monitor_custom_host_enabled');
    const {fields, append, remove} = useFieldArray({control, name: 'headers'});
    const [rows, mode] = useWatch({
        control,
        name: ['headers', 'headers_mode'],
    }) as [IRequestHeaderRow[] | undefined, RequestHeadersMode | undefined];
    const isChecked = !selective || mode !== 'clear';
    const {errors} = useFormState({control, name: 'headers'});

    const nameRules = (index: number): RegisterOptions => ({
        validate: (name: string, values: {headers?: IRequestHeaderRow[]}) => (
            validateHeaderName(name ?? '', values.headers?.[index]?.value ?? '')
        ),
    });

    const valueRules = (index: number): RegisterOptions => ({
        deps: `headers.${index}.name`,
    });

    return {
        fields,
        isChecked,
        canAdd: fields.length < REQUEST_HEADERS_MAX,
        addRow: () => append(EMPTY_ROW),
        removeRow: remove,
        nameRules,
        valueRules,
        isUserAgentLocked: (index: number) => isUserAgentLocked(rows?.[index]?.name ?? '', userAgentEnabled),
        isHostLocked: (index: number) => isHostLocked(rows?.[index]?.name ?? '', customHostEnabled),
        getNameError: (index: number) => getHeaderNameError(errors.headers, index),
        error: getHeadersErrorMessage(errors.headers),
        hasErrors: !!errors.headers,
    };
};

export default useRequestHeaders;
